import styled from 'styled-components'
import alreadyDrink from '../../assets/chart/already-drink.svg'
import { LineChart, Caption } from '../../components'
import { useTranslation } from "react-i18next"

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PredictionChartWrapper = ({
  predictionResult,
  userShifts,
  sleepTime,
  graphSize,
  ingestedCaffeine,
  bacLevels,
}) => {
  const dateNow = new Date()
  const { t } = useTranslation('common')

  const labels = predictionResult.map(item => {
    return item.timestamp
  })

  const historicalData = predictionResult.map(item => {
    if (item.timestamp <= dateNow.getTime()) return item.predictionResult

    return null
  })

  const predictionData = predictionResult.map(item => {
    if (item.timestamp <= dateNow.getTime()) return null

    return item.predictionResult
  })

  const shiftData = userShifts.map(item => {
    if (item.working) return item.value

    return null
  })

  const sleepData = sleepTime.map(item => {
    if (item.sleeping) return item.value

    return null
  })

  const timeNow = dateNow.getTime()
  const caffeineIngested = ingestedCaffeine.map(item => {
    if (item.caffeineHour >= timeNow) return

    return {
      x: item.caffeineHour,
      y: 35,
      image: {
        path: alreadyDrink,
        width: 30,
        height: 30,
      },
    }
  })

  const series = [
  { name: t('dashboard.header.series.alert_level'),
    data: historicalData,
    zIndex: 1,
    color: '#000' },
  { name: t('dashboard.header.series.alert_prediction'),
    data: predictionData,
    zIndex: 1,
    color: '#9FA3AA' },
  { name: t('dashboard.header.series.shift'),
    data: shiftData,
    zIndex: 3,
    color: '#E27837' },
  { name: t('dashboard.header.series.sleep'),
    data: sleepData,
    zIndex: 3,
    color: '#3A6CCF' },
]

  const status = {
    good: {
      text: t('dashboard.header.status.good.text'),
      color: t('dashboard.header.status.good.color')
    },
    regular: {
      text: t('dashboard.header.status.regular.text'),
      color: t('dashboard.header.status.regular.color')
    },
    bad: {
      text: t('dashboard.header.status.bad.text'),
      color: t('dashboard.header.status.bad.color')
    }
  }

  const predictionIndexNow = predictionResult.findIndex(
    item => item.timestamp > timeNow,
  )

  const alertStatus = predictionResult[predictionIndexNow || 0]?.prediction

  const getHoursFormatted = date => {
    return date < 10 ? '0' + date : '' + date
  }

  const bacLevelsPoints = () => {
    const bacBottom = bacLevels?.bottom
    const bacTop = bacLevels?.top

    if (!bacBottom?.active && !bacTop?.active) return {}

    let levels = {}

    if (bacBottom?.active) {
      levels.bacEight = {
        y: Math.round(bacBottom?.value),
        y2: null,
        strokeDashArray: 8,
        fillColor: '#9FA3AA',
        borderColor: '#9FA3AA',
        offsetY: 0,
        label: {
          offsetX: -8,
          borderColor: null,
          text: 'BAC 0.08%',
          textAnchor: 'end',
          position: 'right',
          style: {
            background: '#fff',
            color: '#9FA3AA',
            fontSize: '14px',
            fontWeight: 400,
          },
        },
      }
    }

    if (bacTop?.active) {
      levels.bacSix = {
        y: Math.round(bacTop?.value),
        y2: null,
        strokeDashArray: 8,
        fillColor: '#9FA3AA',
        borderColor: '#9FA3AA',
        offsetY: 0,
        label: {
          offsetX: -8,
          borderColor: null,
          text: 'BAC 0.06%',
          textAnchor: 'end',
          position: 'right',
          style: {
            background: '#fff',
            color: '#9FA3AA',
            fontSize: '14px',
            fontWeight: 400,
          },
        },
      }
    }

    return levels
  }

  const bacLevelsConfig = bacLevelsPoints()

  const graphOptions = {
    annotations: {
      yaxis: [
        {
          y: 30,
          y2: 50,
          borderColor: '#fd564e',
          fillColor: '#fd564e',
          strokeDashArray: 1,
          textAnchor: 'end',
        },
        {
          y: 50,
          y2: 80,
          strokeDashArray: 1,
          borderColor: '#ffe000',
          offsetY: 0,
          fillColor: '#ffe000',
        },
        {
          y: 80,
          y2: 100,
          strokeDashArray: 1,
          borderColor: '#71e74b',
          offsetY: 0,
          fillColor: '#71e74b',
        },
        { ...bacLevelsConfig?.bacEight },
        { ...bacLevelsConfig?.bacSix },
      ],
      xaxis: [
        {
          x: dateNow.getTime(),
          strokeDashArray: 2,
          borderColor: `${status[alertStatus]?.color}`,
          fillColor: `${status[alertStatus]?.color}`,
          label: {
            borderColor: `${status[alertStatus]?.color}`,
            style: {
              color: '#FFF',
              background: `${status[alertStatus]?.color}`,
              fontSize: '16px',
            },
            text: `${getHoursFormatted(dateNow.getHours())}h${getHoursFormatted(dateNow.getMinutes())} - ${t('dashboard.header.level')} ${status[alertStatus]?.text}`,
            orientation: 'horizontal',
          },
        },
      ],
      points: [...caffeineIngested],
    },
    stroke: {
      width: [4, 4, 10, 10],
      dashArray: [0, 0, 0, 0],
    },
    tooltip: {
      enabledOnSeries: [0, 1],
      followCursor: true,
    },
    legend: {
      show: false,
    },
  }

  return (
    <ChartContainer>
      <LineChart
        data={series}
        graphOptions={graphOptions}
        labels={labels}
        width={graphSize}
      />
      <Caption />
    </ChartContainer>
  )
}

export default PredictionChartWrapper
